/**
 * RealTimeActivity - Enterprise Real-time Activity Feed
 * Feed realtime delle attivita di sistema e gaming
 */

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { wsManager } from "@/lib/websocket";
import { apiUrl } from "@/lib/api";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import {
    Activity,
    User,
    Server,
    MessageCircle,
    AlertTriangle,
    Clock,
    Zap,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { it } from "date-fns/locale";

interface ActivityEvent {
    id: string;
    type:
        | "user_activity"
        | "server_event"
        | "system_alert"
        | "chat_message"
        | "admin_action";
    title: string;
    description: string;
    timestamp: Date;
    severity?: "info" | "warning" | "error" | "success";
    metadata?: Record<string, any>;
}

const getEventIcon = (type: string, severity?: string) => {
    switch (type) {
        case "user_activity":
            return <User className="h-3 w-3" />;
        case "server_event":
            return severity === "error" ? (
                <AlertTriangle className="h-3 w-3" />
            ) : (
                <Server className="h-3 w-3" />
            );
        case "system_alert":
            return <AlertTriangle className="h-3 w-3" />;
        case "chat_message":
            return <MessageCircle className="h-3 w-3" />;
        case "admin_action":
            return <Zap className="h-3 w-3" />;
        default:
            return <Activity className="h-3 w-3" />;
    }
};

const getEventColor = (severity?: string) => {
    switch (severity) {
        case "success":
            return "text-green-600";
        case "warning":
            return "text-yellow-600";
        case "error":
            return "text-red-600";
        case "info":
        default:
            return "text-blue-600";
    }
};

const getBadgeVariant = (severity?: string) => {
    switch (severity) {
        case "success":
            return "default";
        case "warning":
            return "secondary";
        case "error":
            return "destructive";
        case "info":
        default:
            return "outline";
    }
};

const _safeDate = (value: unknown): Date | null => {
    const d = new Date(String(value || ""));
    return Number.isNaN(d.getTime()) ? null : d;
};

const _severityLabel = (severity?: string) => {
    switch (severity) {
        case "success":
            return "successo";
        case "warning":
            return "attenzione";
        case "error":
            return "errore";
        case "info":
        default:
            return "info";
    }
};

export const RealTimeActivity: React.FC = () => {
    const [activities, setActivities] = useState<ActivityEvent[]>([]);
    const [isLive, setIsLive] = useState(false);

    useEffect(() => {
        const bootstrap = async () => {
            try {
                const res = await fetch(apiUrl("/api/v1/servers/status/overview"));
                if (!res.ok) return;
                const data = await res.json();
                const alerts = Array.isArray(data?.alerts) ? data.alerts : [];
                const bootEvents: ActivityEvent[] = alerts.slice(0, 25).map((a: any) => ({
                    id: `boot-alert-${a.server_id || "system"}-${a.timestamp || Date.now()}`,
                    type: "system_alert",
                    title: a.type === "error" ? "Server offline" : "Avviso server",
                    description: String(a.message || "Alert"),
                    timestamp: _safeDate(a.timestamp) || new Date(),
                    severity:
                        a.type === "error"
                            ? "error"
                            : a.type === "warning"
                              ? "warning"
                              : "info",
                    metadata: a,
                }));
                setActivities(bootEvents);
            } catch {
                setActivities([]);
            }
        };

        bootstrap();

        const handleServerUpdate = (data: any) => {
            const st =
                typeof data?.status === "string"
                    ? data.status
                    : typeof data?.status?.status === "string"
                      ? data.status.status
                      : "unknown";
            addActivity({
                id: `server-${Date.now()}`,
                type: "server_event",
                title: "Cambio stato server",
                description: `${data.server_id} ora e ${st}`,
                timestamp: new Date(),
                severity:
                    st === "online" ? "success" : st === "offline" ? "error" : "warning",
                metadata: data,
            });
        };

        const handleAlert = (data: any) => {
            addActivity({
                id: `alert-${Date.now()}`,
                type: "system_alert",
                title: `Allarme sistema - ${data.level}`,
                description: String(data.message || "Alert"),
                timestamp: new Date(),
                severity:
                    data.level === "critical"
                        ? "error"
                        : data.level === "warning"
                          ? "warning"
                          : "info",
                metadata: data,
            });
        };

        const handleUserJoined = (data: any) => {
            addActivity({
                id: `user-join-${Date.now()}`,
                type: "user_activity",
                title: "Utente connesso",
                description: `${data.username} si e collegato alla piattaforma`,
                timestamp: new Date(),
                severity: "success",
                metadata: data,
            });
        };

        const handleUserLeft = (data: any) => {
            addActivity({
                id: `user-left-${Date.now()}`,
                type: "user_activity",
                title: "Utente disconnesso",
                description: `${data.username} ha lasciato la piattaforma`,
                timestamp: new Date(),
                severity: "info",
                metadata: data,
            });
        };

        const handleChatMessage = (data: any) => {
            addActivity({
                id: `chat-${Date.now()}`,
                type: "chat_message",
                title: "Messaggio chat",
                description: `${data.username}: ${data.message.substring(0, 50)}${data.message.length > 50 ? "..." : ""}`,
                timestamp: new Date(),
                severity: "info",
                metadata: data,
            });
        };

        wsManager.on("server_status_update", handleServerUpdate);
        wsManager.on("alert", handleAlert);
        wsManager.on("user_joined", handleUserJoined);
        wsManager.on("user_left", handleUserLeft);
        wsManager.on("chat_message", handleChatMessage);

        setIsLive(wsManager.isConnected());
        const statusInterval = setInterval(() => {
            setIsLive(wsManager.isConnected());
        }, 5000);

        return () => {
            wsManager.off("server_status_update", handleServerUpdate);
            wsManager.off("alert", handleAlert);
            wsManager.off("user_joined", handleUserJoined);
            wsManager.off("user_left", handleUserLeft);
            wsManager.off("chat_message", handleChatMessage);
            clearInterval(statusInterval);
        };
    }, []);

    const addActivity = (activity: ActivityEvent) => {
        setActivities((prev) => [activity, ...prev].slice(0, 50));
    };

    return (
        <Card>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                        <Activity className="h-4 w-4" />
                        Attivita realtime
                    </CardTitle>
                    <Badge
                        variant={isLive ? "default" : "secondary"}
                        className={`text-xs ${isLive ? "bg-green-100 text-green-800 border-green-200" : ""}`}
                    >
                        {isLive ? (
                            <>
                                <div className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></div>
                                Live
                            </>
                        ) : (
                            "Offline"
                        )}
                    </Badge>
                </div>
            </CardHeader>

            <CardContent>
                {activities.length === 0 ? (
                    <div className="text-center py-8">
                        <Activity className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">
                            Nessuna attivita recente
                        </p>
                    </div>
                ) : (
                    <ScrollArea className="h-80">
                        <div className="space-y-3">
                            <AnimatePresence>
                                {activities.map((activity, index) => (
                                    <motion.div
                                        key={activity.id}
                                        initial={{ opacity: 0, x: -20, scale: 0.95 }}
                                        animate={{ opacity: 1, x: 0, scale: 1 }}
                                        exit={{ opacity: 0, x: 20, scale: 0.95 }}
                                        transition={{ duration: 0.3, delay: index * 0.05 }}
                                        className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border"
                                    >
                                        <div className={`mt-0.5 ${getEventColor(activity.severity)}`}>
                                            {getEventIcon(activity.type, activity.severity)}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h4 className="text-sm font-medium text-foreground truncate">
                                                    {activity.title}
                                                </h4>
                                                <Badge
                                                    variant={getBadgeVariant(activity.severity)}
                                                    className="text-xs"
                                                >
                                                    {_severityLabel(activity.severity)}
                                                </Badge>
                                            </div>

                                            <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                                                {activity.description}
                                            </p>

                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                <Clock className="h-3 w-3" />
                                                <span>
                                                    {Number.isNaN(activity.timestamp.getTime())
                                                        ? "N/A"
                                                        : formatDistanceToNow(activity.timestamp, {
                                                              locale: it,
                                                              addSuffix: true,
                                                          })}
                                                </span>

                                                {activity.metadata?.server && (
                                                    <>
                                                        <span>•</span>
                                                        <span>{activity.metadata.server}</span>
                                                    </>
                                                )}

                                                {activity.metadata?.username && (
                                                    <>
                                                        <span>•</span>
                                                        <span>@{activity.metadata.username}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </ScrollArea>
                )}

                {activities.length > 0 && (
                    <div className="mt-4 pt-3 border-t">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>{activities.length} eventi recenti</span>
                            <span>Aggiornamento ogni 30s</span>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

